/** Providers the embed player knows how to mount. */
export type EmbedProvider = 'youtube' | 'vimeo';

/** Events an adapter (in-page SDK or bridge) reports, normalized across providers so the component
 *  never branches on which one it is holding. */
export type EmbedPlayerEvent =
  | { type: 'ready' }
  | { type: 'playing' }
  | { type: 'paused'; currentTime: number }
  | { type: 'ended'; currentTime: number }
  | { type: 'error'; error: unknown };

export interface EmbedAdapterOptions {
  provider: EmbedProvider;
  videoId: string;
  playerId: string;
  startAt: number;
  onEvent: (event: EmbedPlayerEvent) => void;
}

/**
 * What mounts a provider inside the player: an in-page SDK adapter (`youtube.adapter.ts` /
 * `vimeo.adapter.ts`) or the cross-platform bridge (`bridge.adapter.ts`). All three speak this same
 * shape, so `EmbedVideoPlayerComponent` never knows which one it is holding.
 */
export interface EmbedVideoAdapter {
  mount(host: HTMLElement, options: EmbedAdapterOptions): void;
  getCurrentTime(): number;
  getDuration(): number;
  seekTo(seconds: number): void;
  play(): void;
  pause(): void;
  /** Tears down everything this adapter attached: listeners, the player instance, the DOM it added. */
  destroy(): void;
}

/** What the tracker reports on every sample — `position` is what feeds `last_media_duration`. */
export interface WatchProgress {
  position: number;
  duration: number;
  coverage: number;
  nearEnd: boolean;
}

// ---------------------------------------------------------------------------------------------
// The bridge's `postMessage` protocol — shared by the parent (`bridge.adapter.ts`) and the page it
// embeds (`media-embed-bridge.page.ts`), so there is exactly one definition of the wire shape.
// ---------------------------------------------------------------------------------------------

export const EMBED_BRIDGE_SOURCE = 'cursalab-embed' as const;
export const EMBED_BRIDGE_VERSION = 1 as const;

export type EmbedBridgeOutboundType = 'ready' | 'playing' | 'paused' | 'ended' | 'time' | 'error';
export type EmbedBridgeInboundType = 'seek' | 'play' | 'pause';

/** Parent → bridge iframe. */
export interface EmbedBridgeCommand {
  source: typeof EMBED_BRIDGE_SOURCE;
  v: typeof EMBED_BRIDGE_VERSION;
  type: EmbedBridgeInboundType;
  seconds?: number;
}

/** Bridge iframe → parent. */
export interface EmbedBridgeMessage {
  source: typeof EMBED_BRIDGE_SOURCE;
  v: typeof EMBED_BRIDGE_VERSION;
  type: EmbedBridgeOutboundType;
  provider: EmbedProvider;
  id: string;
  currentTime?: number;
  duration?: number;
  coverage?: number;
  error?: string;
}

/** Bridge iframe ↔ parent, before telling which direction it travels — both share `source`/`v`. */
type EmbedBridgeEnvelope = Pick<EmbedBridgeMessage, 'source' | 'v'>;

function isEmbedBridgeEnvelope(data: unknown): data is EmbedBridgeEnvelope {
  const value = data as EmbedBridgeEnvelope | null;
  return !!value && value.source === EMBED_BRIDGE_SOURCE && value.v === EMBED_BRIDGE_VERSION;
}

/** Parent-side: narrows an incoming `message` event to the bridge → parent shape. */
export function isEmbedBridgeMessage(data: unknown): data is EmbedBridgeMessage {
  return isEmbedBridgeEnvelope(data);
}

/** Bridge-page-side: narrows an incoming `message` event to the parent → bridge shape. */
export function isEmbedBridgeCommand(data: unknown): data is EmbedBridgeCommand {
  return isEmbedBridgeEnvelope(data);
}
